import React from "react";
import { Avatar, Card, Col, Row, Skeleton, Typography } from "antd";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@dowty/ui";
import "./ChoiceOverview.css";

type ChoiceOverviewItem = {
    to: string;
    icon: React.ReactNode;
    title: string;
    description?: string;
};

export type GameSuggestion = {
    to: string;
    labelKey: string;
    icon: React.ReactNode;
    descriptionKey?: string;
};

type ChoiceOverviewProps = {
    title: string;
    subtitle: React.ReactNode;
    question?: string;
    items: ChoiceOverviewItem[];
    variant: "drinkhelper" | "lieing";
    selectedTo?: string;
    onSelect?: (to: string) => void;
    suggestions?: GameSuggestion[];
    suggestionTitle?: string;
    playerCount?: string;
    loading?: boolean;
};

const ChoiceOverview: React.FC<ChoiceOverviewProps> = ({
    title, subtitle, question, items, variant, selectedTo, onSelect, suggestions,
    suggestionTitle, playerCount, loading = false,
}) => {
    const { t } = useTranslation();
    const isDrinkhelper = variant === "drinkhelper";
    return (
        <div className={`choice-overview${isDrinkhelper ? " choice-overview-home" : ""}`}>
            {(title || subtitle) && (
                <PageHeader title={title} subtitle={subtitle} />
            )}

            {question && <Typography.Title level={2} className="choice-overview-question">{question}</Typography.Title>}
            {isDrinkhelper && onSelect ? (
                <fieldset className="choice-overview-options" aria-label={question}>
                    {items.map((item) => (
                        <button
                            type="button"
                            aria-pressed={selectedTo === item.to}
                            className="choice-overview-option"
                            key={item.to}
                            onClick={() => onSelect(item.to)}
                        >
                            <Avatar aria-hidden="true" icon={item.icon} />
                            <span className="choice-overview-option-title">{item.title}</span>
                            {item.description && <span className="choice-overview-option-hint">{item.description}</span>}
                        </button>
                    ))}
                </fieldset>
            ) : (
                <Row gutter={[16, 16]} justify="center">
                    {items.map((item) => (
                        <Col xs={24} sm={12} lg={8} key={item.to}>
                            <Link to={item.to} className="choice-overview-link">
                                <Card hoverable className="choice-overview-card">
                                    <Avatar aria-hidden="true" icon={item.icon} />
                                    <Typography.Text strong>{item.title}</Typography.Text>
                                    {item.description && <Typography.Paragraph>{item.description}</Typography.Paragraph>}
                                </Card>
                            </Link>
                        </Col>
                    ))}
                </Row>
            )}

            {suggestions && (
                <section className="choice-overview-suggestions" aria-labelledby="suggestions-title" aria-live="polite">
                    <div className="choice-overview-suggestions-heading">
                        <Typography.Title id="suggestions-title" level={2}>{suggestionTitle}</Typography.Title>
                        {playerCount && <Typography.Text>{playerCount}</Typography.Text>}
                    </div>
                    {loading && (
                        <output className="choice-overview-games" aria-label={t("pages.drinkhelper.loading")} aria-busy="true">
                            {Array.from({ length: 3 }, (_, index) => (
                                <Card className="choice-overview-game" key={`skeleton-${index}`}><Skeleton active title paragraph={{ rows: 2 }} /></Card>
                            ))}
                        </output>
                    )}
                    {!loading && suggestions.length === 0 && (
                        <Card className="choice-overview-empty"><output>{t("pages.drinkhelper.empty")}</output></Card>
                    )}
                    {!loading && suggestions.length > 0 && (
                        <div className="choice-overview-games">
                            {suggestions.map((game) => {
                                const descriptionKey = game.descriptionKey || `games.details.${game.labelKey.split(".").pop()}.description`;
                                const detailDescription = t(descriptionKey);
                                const homeDescriptionKey = `pages.drinkhelper.gameDescriptions.${game.labelKey.split(".").pop()}`;
                                const homeDescription = t(homeDescriptionKey);
                                let description = t("pages.drinkhelper.gameHint");
                                if (homeDescription !== homeDescriptionKey) description = homeDescription;
                                if (detailDescription !== descriptionKey) description = detailDescription;
                                if (game.descriptionKey) description = detailDescription;
                                return (
                                    <Link key={game.to} to={game.to} className="choice-overview-game-link">
                                        <Card hoverable className="choice-overview-game">
                                            <div className="choice-overview-game-copy">
                                                <Typography.Text strong>{t(game.labelKey)}</Typography.Text>
                                                <Typography.Paragraph>{description}</Typography.Paragraph>
                                            </div>
                                        </Card>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </section>
            )}
        </div>
    );
};

export default ChoiceOverview;
